import { WalkthroughPreferences } from '../interfaces/walkthrough';
import { AsyncStorage } from '../utils/asyncStorage';

const WALKTHROUGH_STORAGE_KEY = 'cirra_walkthrough_preferences';
const WALKTHROUGH_VERSION = '1.0.0';

export class WalkthroughService {
  // Cache for synchronous access after initial load
  private static preferencesCache: WalkthroughPreferences | null = null;
  private static cachePromise: Promise<WalkthroughPreferences> | null = null;

  static async getPreferences(): Promise<WalkthroughPreferences> {
    // Return cached value if available
    if (this.preferencesCache) {
      return this.preferencesCache;
    }

    // If already loading, return existing promise
    if (this.cachePromise) {
      return this.cachePromise;
    }

    // Load preferences asynchronously
    this.cachePromise = AsyncStorage.getItem(WALKTHROUGH_STORAGE_KEY)
      .then((stored) => {
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            this.preferencesCache = parsed;
            return parsed;
          } catch (error) {
            console.warn('Failed to parse walkthrough preferences:', error);
          }
        }

        const defaultPrefs = {
          hasSeenWalkthrough: false,
          dontShowAgain: false,
          lastSeenVersion: undefined,
        };
        this.preferencesCache = defaultPrefs;
        return defaultPrefs;
      })
      .finally(() => {
        this.cachePromise = null;
      });

    return this.cachePromise;
  }

  // Synchronous version for compatibility - returns cached value or defaults
  static getPreferencesSync(): WalkthroughPreferences {
    if (this.preferencesCache) {
      return this.preferencesCache;
    }

    // Trigger async load for next time
    this.getPreferences();

    // Return defaults for now
    return {
      hasSeenWalkthrough: false,
      dontShowAgain: false,
      lastSeenVersion: undefined,
    };
  }

  static async setPreferences(
    preferences: WalkthroughPreferences
  ): Promise<void> {
    const prefsWithVersion = {
      ...preferences,
      lastSeenVersion: WALKTHROUGH_VERSION,
    };

    // Update cache immediately
    this.preferencesCache = prefsWithVersion;

    try {
      await AsyncStorage.setItem(
        WALKTHROUGH_STORAGE_KEY,
        JSON.stringify(prefsWithVersion)
      );
    } catch (error) {
      console.warn('Failed to save walkthrough preferences:', error);
      throw error;
    }
  }

  static async shouldShowWalkthrough(): Promise<boolean> {
    try {
      const preferences = await this.getPreferences();

      // Don't show if user opted out
      if (preferences.dontShowAgain) {
        return false;
      }

      // Show if never seen before
      if (!preferences.hasSeenWalkthrough) {
        return true;
      }

      // Show if version has changed (for future updates)
      const shouldShow = preferences.lastSeenVersion !== WALKTHROUGH_VERSION;
      return shouldShow;
    } catch (error) {
      console.error('Error in shouldShowWalkthrough:', error);
      // Default to showing walkthrough if there's an error
      return true;
    }
  }

  static async saveProgress(stepIndex: number): Promise<void> {
    const current = await this.getPreferences();
    const updated = {
      ...current,
      currentStepIndex: stepIndex,
    };

    // Update cache immediately
    this.preferencesCache = updated;

    try {
      await AsyncStorage.setItem(
        WALKTHROUGH_STORAGE_KEY,
        JSON.stringify(updated)
      );
    } catch (error) {
      console.warn('Failed to save walkthrough progress:', error);
      throw error;
    }
  }

  static async getSavedStepIndex(): Promise<number> {
    const prefs = await this.getPreferences();
    return prefs.currentStepIndex ?? 0;
  }

  static async markAsCompleted(dontShowAgain = false): Promise<void> {
    await this.setPreferences({
      hasSeenWalkthrough: true,
      dontShowAgain,
      lastSeenVersion: WALKTHROUGH_VERSION,
      currentStepIndex: undefined,
    });
  }

  static async reset(): Promise<void> {
    this.preferencesCache = null;
    await AsyncStorage.removeItem(WALKTHROUGH_STORAGE_KEY);
  }
}
